<script lang="ts" setup name="AdminScheduleRecipientListNew">
import { computed, reactive, ref, watch } from 'vue';

import { getDateYear } from '~/features/useDates';
import { useLeavePageWarning } from '~/features/useLeavePageWarning';
import {
  extractUniqueTradelaneIds,
  getRecipientListDefaultSchema,
  getRecipientListNewForm,
  useRecipientListSteps,
} from '~/features/useRecipientListForm';

import Service from '~/services/Service.js';

import AdminSailingScheduleRecipientListNewSuccess from '~/components/AdminSailingScheduleRecipientListNewSuccess.vue';
import AdminScheduleRecipientListNewEmailForm from '~/components/AdminScheduleRecipientListNewEmailForm.vue';
import AdminScheduleRecipientListNewSailingSchedule from '~/components/AdminScheduleRecipientListNewSailingSchedule.vue';
import AdminScheduleRecipientsListSettingsNewForm from '~/components/AdminScheduleRecipientsListSettingsNewForm.vue';
import BasePage from '~/components/BasePage.vue';
import FieldFormSection from '~/components/FieldFormSection.vue';
import FormWizard from '~/components/FormWizard.vue';
import FormWizardStep from '~/components/FormWizardStep.vue';

import type { IRecipientListForm } from '~/types';

const page = computed(() => {
  const title = form.name
    ? `Create New Recipient List of ${form.name}`
    : 'Create New Recipient List';

  return {
    title,
    backButton: {
      label: 'Recipient list',
      to: { name: 'AdminScheduleRecipientList' },
    },
  };
});

const schema = reactive(getRecipientListDefaultSchema());
const tradelanes = computed(() => extractUniqueTradelaneIds(form.tradelanes));

const finished = ref(false);
const submitting = ref(false);
const submitError = ref(false);
const errors = ref<Record<string, string>>({});

const initialForm = getRecipientListNewForm();
const form = reactive<IRecipientListForm>(getRecipientListNewForm());

const steps = useRecipientListSteps(form);

const onSubmit = () => {
  const formatRecipientListData = (formData: IRecipientListForm) => {
    return {
      ...formData,
      message: btoa(form.message),
      startRecurringDate: getDateYear(form.startRecurringDate),
      tradelanes: extractUniqueTradelaneIds(form.tradelanes),
      settings: form.settings,
    };
  };

  const formattedData = formatRecipientListData(form);

  Service.recipientList()
    .store(formattedData)
    .onStart(() => {
      submitting.value = true;
    })
    .onError(() => {
      submitError.value = true;
      submitting.value = false;
    })
    .onSuccess(() => {
      finished.value = true;
      submitting.value = false;
    })
    .onErrorValidation((data) => {
      errors.value = data.response.data?.errors;
      submitting.value = false;
    })
    .execute();
};

useLeavePageWarning(
  computed(() => JSON.stringify(initialForm) === JSON.stringify(form)),
  finished,
);

watch(
  () => tradelanes.value,
  (newTrades) => {
    form.settings = Object.fromEntries(
      newTrades
        .filter((trade) => form.settings[trade])
        .map((trade) => [trade, form.settings[trade]]),
    );
  },
);
</script>

<template>
  <BasePage v-bind="page">
    <div class="col-span-full">
      <AdminSailingScheduleRecipientListNewSuccess
        v-if="finished"
        :frequency="form.frequency"
        :recipients="form.recipients"
        :startRecurringDate="form.startRecurringDate"
      />
      <FormWizard
        v-else
        :loading="submitting"
        :errors="errors"
        :steps="steps"
        :submit-error="submitError"
        @submit="onSubmit"
      >
        <FormWizardStep>
          <FieldFormSection
            title="Set up Sharing Settings for Sailing Schedule"
          />
          <AdminScheduleRecipientsListSettingsNewForm :form="form" :schema />
        </FormWizardStep>

        <FormWizardStep>
          <FieldFormSection title="Add Recipients" full>
            <AdminScheduleRecipientListNewEmailForm
              v-model="form.recipients"
              checkboxSelection
            />
          </FieldFormSection>
        </FormWizardStep>
        <FormWizardStep>
          <FieldFormSection title="Customise Sailing Schedule" full />
          <AdminScheduleRecipientListNewSailingSchedule
            :recipientService="Service.sailingSchedule()"
            :tradelanes="tradelanes"
            v-model="form.settings"
          />
        </FormWizardStep>
      </FormWizard>
    </div>
  </BasePage>
</template>
